import {
    type FetchQueryOptions,
    keepPreviousData,
    useQuery,
    useQueryClient,
    type UseQueryResult,
} from '@tanstack/react-query';

import {
    DAAS_ACTIVE_LISTINGS,
    DAAS_COLLECTION_TOKEN_COUNT_CACHE,
    type GetMultichainCollectionTokenCountProps,
    getQueryString,
    type MultichainCollectionMetadataProps,
} from '@pkg/hooks';

import { type ActiveListing, type ApiResult, type ChainTokenCount } from '@app/client';
import { callGetActiveListingData, callGetCollectionTokenCount } from '@app/client/resources/daas';

export function useGetMultichainCollectionTokenCount({ slugs, disabled }: GetMultichainCollectionTokenCountProps) {
    const queryClient = useQueryClient();
    const queryTokenCountOptions = (customQuery: string): FetchQueryOptions => {
        const queryCache = [DAAS_COLLECTION_TOKEN_COUNT_CACHE];
        const getQueryCache = (key: string) => [...queryCache, key];
        return {
            queryKey: getQueryCache(customQuery),
            retry: false,
            queryFn: ({ signal }) =>
                callGetCollectionTokenCount({
                    type: 'tokenCount',
                    settings: { signal },
                    customQuery: `?slugs=${customQuery}`,
                }) as Promise<ApiResult<ChainTokenCount>>,
        };
    };

    const queryListingCountOptions = (slugsQuery: string[]): FetchQueryOptions => {
        const queryArrayString = getQueryString({ queryArrayKey: 'slugs', queryArray: slugsQuery });
        return {
            queryKey: [DAAS_ACTIVE_LISTINGS, ...slugsQuery],
            retry: false,
            queryFn: ({ signal }) =>
                callGetActiveListingData({
                    settings: { signal },
                    customQuery: queryArrayString,
                }),
        };
    };

    const queryString = slugs?.sort()?.join('&slugs=');
    const options = queryTokenCountOptions(queryString || '');

    const { data, ...rest } = useQuery({
        ...options,
        select: (result) => {
            const res = result as ApiResult<ChainTokenCount>;
            if (res?.ok && res.value) {
                return res.value;
            }
        },
        placeholderData: keepPreviousData,
        enabled: Boolean(slugs?.length) && !disabled,
    }) as UseQueryResult<ChainTokenCount>;

    const getMultichainTokenCount = ({
        slugQuery = [],
    }: MultichainCollectionMetadataProps): Promise<ApiResult<ChainTokenCount>> | undefined => {
        const queryString = slugQuery.sort()?.join('&slugs=');
        const options = queryTokenCountOptions(queryString);
        if (!slugQuery?.length || disabled) return;

        return queryClient.fetchQuery({
            ...options,
        }) as Promise<ApiResult<ChainTokenCount>>;
    };

    const getMultichainTokenListingsCount = ({
        slugQuery = [],
    }: MultichainCollectionMetadataProps): Promise<ApiResult<ActiveListing>> | undefined => {
        if (!slugQuery?.length || disabled) return;
        const options = queryListingCountOptions(slugQuery);
        return queryClient.fetchQuery({
            ...options,
        }) as Promise<ApiResult<ActiveListing>>;
    };

    return { getMultichainTokenListingsCount, getMultichainTokenCount, data, ...rest };
}
